(ns nosco.ui.tabs2
  "Headless accessible tab components."
  (:require
   ["react" :as React]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.icons2 :as icons2]
   [nosco.react :as nosco-react]
   [nosco.ui.user-menubar :as menubar]
   [nosco.util :as util]
   [nosco.ui.tooltips :as tooltips]))

;; TODO Reconcile this with nosco.ui.tabs

(defonce TabSelectionContext (React/createContext nil))

(defnc BaseTab
  "An unstyled Tab.

  Props:
  - :as - A keyword or element to render the element as
  - :id - A DOM ID for the tab

  When children is a function, it will be called with {:selected true/false}.

  Other props are merged."
  [{:keys [as id children] :or {as :button} :as props}]
  {:pre [(some? id)]}
  (let [{:keys [selected-tab-id on-select]} (hooks/useContext TabSelectionContext)
        selected (= id selected-tab-id)]
    [as
     (nosco-react/merge-props
      (dissoc props :as :selected)
      {:id id
       :role "tab"
       :class [(when (= :button as) "button-reset")
               (when (= :a as) "link-button-reset")]
       :aria-selected selected
       ;; Only the selected tab should be focussable
       :tab-index (if selected 0 -1)
       :onClick (when on-select #(on-select id))})
     (if (fn? children)
       (children {:selected selected})
       children)]))

(defnc Tab
  [{:keys [variant
           size
           label
           icon
           badge
           no-hover
           tooltip
           tooltip-props]
    :or {variant :blue
         size :default}
    :as props}]
  [BaseTab
   (-> {:style {:width "100%"}}
       (nosco-react/merge-props (dissoc props :label :icon :badge :variant :size :no-hover)))
   (fn [{:keys [selected]}]
     [tooltips/WithTooltip (merge {:label tooltip} tooltip-props)
      [:div {:class [(when (not= variant :text) "nosco-tab2")
                     (when selected "nosco-tab2--selected")
                     (when no-hover "nosco-tab2--no-hover")
                     (when (= :text variant) "nosco-tab2-text")
                     (when (= :green variant) "nosco-tab2--variant-green")
                     (when (= :light variant) "nosco-tab2--variant-light")
                     (when (= :dense size) "nosco-tab2--size-dense")]}
       (when icon
         (if (React/isValidElement icon)
           icon
           [icons2/Icon {:icon icon}]))
       (when label
         [:span {:class ["nosco-tab2__label"]}
          label])
       (when badge
         [:span {:class ["nosco-tab2__badge"]}
          [:span badge]])]])])

(defnc TextTab [{:keys [icon label badge tooltip tooltip-props] :as props}]
  [BaseTab
   (dissoc props :label :icon :badge)
   (fn [{:keys [selected]}]
     [tooltips/WithTooltip
      (merge {:label tooltip
              :popper-opts {:placement "bottom" :offset [0 -6]}}
             tooltip-props)
      [:div {:class ["nosco-tab2-text"
                     (when selected "nosco-tab2-text--selected")]}
       (when icon
         (if (React/isValidElement icon)
           icon
           [icons2/Icon {:icon icon}]))
       (when label
         [:span {:class ["nosco-tab2-text__label"]}
          label])
       (when badge
         [:span {:class ["nosco-tab2-text__badge"]}
          [:span badge]])]])])

(defnc TabList
  "An unstyled, accessible tablist.

  Props:
  - :selected-tab-id - The DOM ID of the selected tab
  - :on-select - Called when a new tab is selected
  - :manual - When true, the selected tab is only changed on click or Enter

  Children must be instances of `Tab`.

  Other props are merged."
  [{:keys [selected-tab-id on-select manual children] :as props}]
  (let [tab-list-ref (React/useRef)
        [tab-ids set-tab-ids] (hooks/useState nil)]
    (hooks/useEffect (fn []
                       (when-let [tab-list-node (.-current tab-list-ref)]
                         (let [tab-ids (map #(.-id %) (.querySelectorAll tab-list-node "[role=\"tab\"]"))]
                           (set-tab-ids tab-ids))))
                     [children])
    [:div
     (nosco-react/merge-props
      (dissoc props :selected-tab-id :on-select :manual)
      {:ref tab-list-ref
       :role "tablist"
       :on-key-down
       (fn [^js event]
         (when (#{"ArrowRight" "ArrowLeft"} (.-key event))
           (let [focussed-tab-id (.. js/document -activeElement -id)
                 next-tab-id-wrapping (fn [tab-ids]
                                        (nth tab-ids (mod (inc (util/index-of tab-ids focussed-tab-id)) (count tab-ids))))
                 next-tab-id (case (.-key event)
                               "ArrowRight" (do (.stopPropagation event)
                                                (next-tab-id-wrapping tab-ids))
                               "ArrowLeft" (do
                                             (.stopPropagation event)
                                             (next-tab-id-wrapping (reverse tab-ids))))
                 tab-el (.getElementById js/document next-tab-id)]
             (when (and (not manual) on-select) (on-select next-tab-id))
             (.focus tab-el)
             (when (and (not manual) (instance? js/HTMLAnchorElement tab-el))
               (.click tab-el)))))})
     [TabSelectionContext.Provider
      {:value {:selected-tab-id selected-tab-id
               :on-select on-select}}
      children]]))

(defn- use-tabs [tabs]
  (let [tabs-with-ids (hooks/useMemo (fn [] (mapv (fn [tab] (assoc tab :id (util/genid (name (:key tab))))) tabs)) [tabs])
        tab-key->id (hooks/useMemo (fn [] (into {} (map (juxt :key :id) tabs-with-ids))) [tabs-with-ids])
        tab-id->key (hooks/useMemo (fn [] (into {} (map (juxt :id :key) tabs-with-ids))) [tabs-with-ids])]
    [tabs-with-ids tab-key->id tab-id->key]))

(defnc TabGroup [{:keys [tabs selected-tab on-select variant background-variant]}]
  (let [tab-width (str (/ 100 (count tabs)) "%")
        [tabs-with-ids tab-key->id tab-id->key] (use-tabs tabs)]
    [TabList
     {:selected-tab-id (tab-key->id selected-tab)
      :on-select #(on-select (tab-id->key %))}
     [:div
      {:class ["nosco-tab2-group"]
       :style {:background-color (when (= :white background-variant) "white")}}
      [:div {:class ["nosco-tab2-group__tabs"]}
       (for [tab tabs-with-ids]
         [:div
          {:class ["nosco-tab2-group__tabs__tab-wrap"]
           :style {:width tab-width}}
          [Tab
           (cond-> tab
             (= :green variant) (assoc :variant :green))]])]]]))

(defnc TightTabGroup
  "Tabs of equal size in a tight group, with no scrolling or overflow."
  [{:keys [tabs selected-tab on-select class]}]
  (let [[tabs-with-ids tab-key->id tab-id->key] (use-tabs tabs)]
    [TabList
     {:selected-tab-id (tab-key->id selected-tab)
      :class class
      :on-select #(on-select (tab-id->key %))}
     [:div {:class ["nosco-tab2-tight-group"]}
      (for [tab tabs-with-ids]
        [Tab
         (assoc tab
                :variant :light
                :size :dense
                :no-hover true)])]]))

(defnc AdminTextTabs [{:keys [tabs selected-tab on-select class]}]
  (let [[tabs-with-ids tab-key->id tab-id->key] (use-tabs tabs)]
    [TabList
     {:selected-tab-id (tab-key->id selected-tab)
      :class class
      :on-select #(on-select (tab-id->key %))}
     [:div {:class ["nosco-admin-text-tabs-group"]}
      (for [tab tabs-with-ids]
        [TextTab tab])]]))

(defnc MobileHeaderTabs [{:keys [tabs selected-tab on-select sticky]}]
  (let [[tabs tab-key->id tab-id->key] (use-tabs tabs)
        sticky-showing (hooks/useContext menubar/ScrollDirectionShowingContext)]
    [TabList
     {:class ["nosco-mobile-header-tabs"
              (when sticky "nosco-mobile-header-tabs--sticky")
              (when (and sticky (not sticky-showing)) "nosco-mobile-header-tabs--sticky-hidden")]
      :selected-tab-id (tab-key->id selected-tab)
      :on-select (when on-select #(on-select (tab-id->key %)))}
     (for [{:keys [label] :as tab} tabs]
       [BaseTab
        (assoc tab
               :class "nosco-mobile-header-tabs__tab")
        label])
     #_[:button {:class "button-reset nosco-mobile-header-tabs__tab"}
        (t/t "More")
        [:span {:class "ml-spacing-4"}
         [icons2/Icon {:icon icons2/DropdownSm-small}]]]]))
